import { createContext, type PropsWithChildren, useContext, useMemo } from 'react';

import { useGetConfigQuery } from '../queries/config-queries';
import type { Config } from '../types/commons';

type ConfigContextType = {
  config: Config | undefined;
  isLoadingConfig: boolean;
  configError: Error | null;
};

export const ConfigContext = createContext<ConfigContextType>({
  config: undefined,
  isLoadingConfig: true,
  configError: null,
});

// This context provider is based on this doc: https://tkdodo.eu/blog/react-query-and-react-context
// Here we don't handle loading / error but we publish them. This is to avoid a waterfall effect between all the
// similar providers. It's up to the client code to have some loader component that will check the loading status
// of all these providers and make the app available only when they are loaded
export const ConfigProvider = ({ children }: PropsWithChildren) => {
  const { config, configError, isLoadingConfig } = useGetConfigQuery();

  const contextValue = useMemo(
    () => ({ config, isLoadingConfig, configError }),
    [config, isLoadingConfig, configError],
  );

  return <ConfigContext.Provider value={contextValue}>{children}</ConfigContext.Provider>;
};

export const useConfig = () => {
  const { config } = useContext(ConfigContext);

  if (!config) throw Error('Config should be defined here.');

  return config;
};
